import { getFieldSchemaDataList, FieldInput, FieldSchemaData } from "@/utils/getFieldSchemaData";
import { useFieldSchemaV2FactoryContext } from "@ucl/library";
import {
  FinancialApplicationFormSectionSummaryDataRow,
  FinancialApplicationFormSectionSummaryDataRowType,
} from "./FinancialApplicationFormSectionSummary";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { JointAccountOwner, PersonalAccountBeneficiary } from "@/types";
import { KeyPerson } from "@/types/KeyPerson";

/**
 * Filters out empty values from summary data
 * @param data - Array of summary data rows
 * @param filterEmptyValues - Whether to filter out empty values
 * @returns Filtered array of summary data rows
 */
const filterEmptySummaryValues = (
  data: FinancialApplicationFormSectionSummaryDataRow[],
  filterEmptyValues: boolean
): FinancialApplicationFormSectionSummaryDataRow[] => {
  if (!filterEmptyValues) {
    return data;
  }

  return data.filter(item => {
    if (item.type === FinancialApplicationFormSectionSummaryDataRowType.LabelValue) {
      return item.value && item.value !== "-" && item.value.trim() !== "";
    }
    // Keep toggle items
    return true;
  });
};

export const useFieldSchemaToGetDataForSummary = (
  financialApplicationId: string,
  fieldInputs: FieldInput[],
  filterEmptyValues: boolean = true
) => {
  const { formValue } = useFinancialApplicationForm(financialApplicationId);
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  if (!formValue || !schemaFactory) {
    return [];
  }

  // Use the new utility function to get field schema data
  // The utility will automatically expand complex fields into their sub-fields
  const fieldData = getFieldSchemaDataList(fieldInputs, formValue, schemaFactory);

  // Transform the field data into the format expected by FinancialApplicationFormSectionSummary
  // Since complex fields are now expanded, we can use the field data directly
  const summaryData: FinancialApplicationFormSectionSummaryDataRow[] = fieldData.map((field) => {
    if (field.type === "Switch") {
      return {
        label: field.label,
        value: field.value,
        type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue,
      };
    }

    return {
      label: field.label,
      value: field.formattedValue || field.value?.toString() || "-",
      type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
    };
  });

  // Filter out empty values if requested
  return filterEmptySummaryValues(summaryData, filterEmptyValues);
};

export const useFieldSchemaToGetDataForJointOwnerSummary = (
  formValue: JointAccountOwner,
  fieldInputs: FieldInput[],
  filterEmptyValues: boolean = true
) => {
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  if (!formValue || !schemaFactory) {
    return [];
  }

  const fieldData = getFieldSchemaDataList(fieldInputs, formValue, schemaFactory, {
    modelName: "JointAccountOwnerExpressionSchema",
  });

  const fullNameHandlingNulls = (!formValue.personInformation?.firstName?.length && !formValue.personInformation?.lastName?.length) ? "New Joint Account Owner" : `${formValue.personInformation?.firstName} ${formValue.personInformation?.lastName}`;

  const summaryLabel: FinancialApplicationFormSectionSummaryDataRow = {
    label: <b style={{ fontSize: "1rem", fontWeight: "700" }}>{fullNameHandlingNulls} - Joint Owner</b>,
    value: "",
    type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
  };

  const summaryData: FinancialApplicationFormSectionSummaryDataRow[] = fieldData.map((field) => {
    if (field.type === "Switch") {
      return {
        label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
        value: field.value,
        type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue,
      };
    }

    return {
      label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
      value: field.formattedValue || field.value?.toString() || "-",
      type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
    };
  });

  // Filter out empty values if requested
  const filteredSummaryData = filterEmptySummaryValues(summaryData, filterEmptyValues);
  return [summaryLabel, ...filteredSummaryData];
};

export const useFieldSchemaToGetDataForPersonalAccountBeneficiarySummary = (
  formValue: PersonalAccountBeneficiary,
  fieldInputs: FieldInput[],
  filterEmptyValues: boolean = true
) => {
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  if (!formValue || !schemaFactory) {
    return [];
  }

  const fieldData = getFieldSchemaDataList(fieldInputs, formValue, schemaFactory, {
    modelName: "PersonalAccountBeneficiaryExpressionSchema",
  });

  let bequestSummaryValue = "";

  if (formValue.personalAccountBequests) {
    const bequestStrings = formValue.personalAccountBequests.map(
      bequest => bequest.financialProductName + " - " + (bequest.percentageBequeathed?.toFixed(2) || "0.00") + "%"
    );
    bequestSummaryValue = bequestStrings.join(", ");
  }

  const bequestFieldData: FieldSchemaData = {
    label: "Beneficiary Designations",
    value: bequestSummaryValue,
    fieldKey: "",
  };

  const allFieldsData = [...fieldData, bequestFieldData];

  const fullNameHandlingNulls = (!formValue.personInformation?.firstName?.length && !formValue.personInformation?.lastName?.length) ? "New Beneficiary" : `${formValue.personInformation?.firstName} ${formValue.personInformation?.lastName}`;

  const summaryLabel: FinancialApplicationFormSectionSummaryDataRow = {
    label: <b style={{ fontSize: "1rem", fontWeight: "700" }}>{fullNameHandlingNulls} - Beneficiary</b>,
    value: "",
    type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
  };

  const summaryData: FinancialApplicationFormSectionSummaryDataRow[] = allFieldsData.map((field) => {
    if (field.type === "Switch") {
      return {
        label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
        value: field.value,
        type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue,
      };
    }

    return {
      label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
      value: field.formattedValue || field.value?.toString() || "-",
      type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
    };
  });

  // Filter out empty values if requested
  const filteredSummaryData = filterEmptySummaryValues(summaryData, filterEmptyValues);
  return [summaryLabel, ...filteredSummaryData];
};

export const useFieldSchemaToGetDataForKeyPersonSummary = (
  formValue: KeyPerson,
  fieldInputs: FieldInput[],
  filterEmptyValues: boolean = true
) => {
  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  if (!formValue || !schemaFactory) {
    return [];
  }

  const fieldData = getFieldSchemaDataList(fieldInputs, formValue, schemaFactory, {
    modelName: "KeyPersonExpressionSchema",
  });

  // Build the role label based on selected roles
  const roles = [];
  if (formValue.isBeneficialOwner) {
    roles.push(`Beneficial Owner`);
  }
  if (formValue.isAuthorizedSigner) {
    roles.push("Authorized Signer");
  }
  if (formValue.isControlPerson) {
    roles.push("Control Person");
  }

  let roleLabel = "";
  if (roles.length === 0) {
    roleLabel = "No roles selected";
  } else if (roles.length === 1) {
    roleLabel = roles[0];
  } else if (roles.length === 2) {
    roleLabel = `${roles[0]} and ${roles[1]}`;
  } else {
    roleLabel = `${roles.slice(0, -1).join(", ")}, and ${roles[roles.length - 1]}`;
  }

  const fullNameHandlingNulls = (!formValue.personInformation?.firstName?.length && !formValue.personInformation?.lastName?.length) ? "New Key Person" : `${formValue.personInformation?.firstName} ${formValue.personInformation?.lastName}`;

  const summaryLabel: FinancialApplicationFormSectionSummaryDataRow = {
    label: <b style={{ fontSize: "1rem", fontWeight: "700" }}>{fullNameHandlingNulls} - {roleLabel}</b>,
    value: "",
    type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
  };

  const summaryData: FinancialApplicationFormSectionSummaryDataRow[] = fieldData.map((field) => {
    if (field.type === "Switch") {
      return {
        label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
        value: field.value,
        type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue,
      };
    }

    return {
      label: <span style={{ marginLeft: "2rem" }}>{field.label}</span>,
      value: field.formattedValue || field.value?.toString() || "-",
      type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
    };
  });

  // Filter out empty values if requested
  const filteredSummaryData = filterEmptySummaryValues(summaryData, filterEmptyValues);
  return [summaryLabel, ...filteredSummaryData];
};
